import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import colors from '@c2fo/liquidity/colors';
import { SyncIcon } from '@c2fo/liquidity/icons';
import useUserAccountValues from '@/accountSettings/utils/useUserAccountValues';
import grocerybasket from '@/enterprise/assets/grocerybasket.svg?url';
import {
  createOfferBuilderInitialState,
  createPrioritizedDisplayOrder,
  useOfferBuilderContext,
  OfferBuilderProvider,
  CreateOfferBuilderInitialStateOptions,
  OfferBuilderState,
} from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { OfferBuilderErrorState } from '@/enterprise/components/OfferBuilder/OfferBuilderErrorState';
import {
  SetRateModalProvider,
  useSetRateModal,
} from '@/enterprise/components/OfferBuilder/OfferReview/SetRateModalContext';
import {
  RateTypeSelectPortalProvider,
  useRateTypeSelectPortal,
} from '@/enterprise/components/OfferBuilder/RateTypeSelectPortalContext';
import SetRateModal from '@/enterprise/components/OfferBuilder/SetRateModal';
import { ensureWorkingTakerMarkets } from '@/enterprise/components/OfferBuilder/utils';
import Skeleton from '@/enterprise/components/Skeleton';
import useGroupByBuyer from '@/enterprise/data/useGroupByBuyer';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { ButtonLink } from '@/shared/components/ButtonLink';
import NavigateWithRedirect from '@/shared/components/NavigateWithRedirect';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import NoDataState from '../components/NoDataState';
import { getCurrentStep, isConfirmationPage, getOfferBuilderSteps } from '../components/OfferBuilder/OfferBuilderSteps';
import usePreferredRateEligibility from '../components/OfferBuilder/usePreferredRateEligibility';

/**
 * offer builder navigation state options
 * extends CreateOfferBuilderInitialStateOptions with preselected taker market IDs
 * excludes 'selected' (use preselectedTakerMarketIds instead) and 'initialDisplayOrder'
 * (computed internally from buyers and selected items)
 */
export type OfferBuilderNavigationState = Omit<
  CreateOfferBuilderInitialStateOptions,
  'selected' | 'initialDisplayOrder'
> & {
  preselectedTakerMarketIds?: string[];
};

/**
 * options for creating initial state from preselection
 * same as OfferBuilderNavigationState but without preselectedTakerMarketIds
 * since those are passed as a separate parameter
 */
type OfferBuilderPreselectionOptions = Omit<OfferBuilderNavigationState, 'preselectedTakerMarketIds'>;

const OfferBuilderError = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <OfferBuilderErrorState
      pageHeading={t('core.earlyPayOffer')}
      secondaryHeading={t('builder.error')}
      additionalActions={
        <div className="flex items-center justify-between gap-4 rounded-md bg-white px-6 py-4 text-left shadow-md lg:w-1/3">
          <div>
            <p className="text-sm font-medium">{t('core.returnToMarkets')}</p>
            <p className="text-sm text-text-secondary">{t('core.returnToMarketsDescription')}</p>
          </div>
          <button
            className="rounded-md p-4 hover:bg-gray-100"
            onClick={() => navigate(asEnterpriseLink('customers'))}
            type="button"
          >
            <SyncIcon className="h-7 w-auto lg:block" fill={colors.blue[800]} />
          </button>
        </div>
      }
    />
  );
};

const OfferBuilderSkeleton = () => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="flex flex-col items-start justify-between gap-6 pb-10 md:flex-row md:items-center">
        <h1 className="m-0 text-4xl">{t('core.earlyPayOffer')}</h1>
      </div>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="lg:w-7/12">
          <div className="space-y-4 rounded-xl bg-deepBlue-50 p-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-24 w-full" />
            ))}
          </div>
        </div>
        <div className="flex-1">
          <div className="rounded-lg bg-deepBlue-50 p-4">
            <Skeleton className="h-60 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

const SetRateModalRenderer = () => {
  const { state, closeModal } = useSetRateModal();

  const getModalProps = () => {
    const baseProps = { open: state.isOpen, onClose: closeModal };

    switch (state.mode) {
      case 'initial':
        return { ...baseProps, mode: 'initial' as const, onNext: state.onNext! };
      case 'summary':
        return { ...baseProps, mode: 'summary' as const, initialRateType: state.initialRateType, onNext: state.onNext };
      case 'buyer':
        return { ...baseProps, mode: 'buyer' as const, buyer: state.buyer! };
      case 'takerMarket':
        return { ...baseProps, mode: 'takerMarket' as const, takerMarket: state.takerMarket! };
      default:
        return { ...baseProps, mode: undefined, buyer: undefined, takerMarket: undefined };
    }
  };

  return <SetRateModal {...getModalProps()} />;
};

const InitialStepNavigator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useOfferBuilderContext();

  useEffectOnce(() => {
    // only navigate if we have an initialStep that's not the default step
    // and currently at the index route
    if (
      state.initialStep !== null &&
      state.initialStep !== '' &&
      location.pathname === asEnterpriseLink('build-offer')
    ) {
      navigate(asEnterpriseLink(`build-offer/${state.initialStep}`), { replace: true });
    }
  });

  return null;
};

const OfferBuilderLayout = ({ hasOfferableMarkets }: { hasOfferableMarkets: boolean }) => {
  const { t } = useTranslation();
  const { portalRef } = useRateTypeSelectPortal();
  const location = useLocation();
  const { state } = useOfferBuilderContext();
  const { disableNavigation, submissionInProgress, submissionComplete } = state;
  // Only `includePreferred` is used here — `showPreferredStep` is intentionally omitted
  // since pricingData is not fetched at this level. Passing pricingData would be needed
  // if `showPreferredStep` were used.
  const { includePreferred } = usePreferredRateEligibility(state);
  // Show 5 steps when the user is on or has passed through the preferred-upgrade step.
  // If the step was skipped (selected rate ≤ preferred rate), the user goes directly to
  // review and the step count should remain 4.
  const isOnOrPastPreferredStep = location.pathname.includes('/preferred-upgrade') || state.preferredRateSelected;
  const showPreferredInSteps = includePreferred && isOnOrPastPreferredStep;
  const currentStep = getCurrentStep(location.pathname, showPreferredInSteps);
  const totalSteps = getOfferBuilderSteps(showPreferredInSteps).length;
  const isOnConfirmationPage = isConfirmationPage(location.pathname);
  const { email } = useUserAccountValues();

  // Show no-data state when there are no offerable markets, but only when not in the middle of
  // submitting or after a successful submission (e.g. last market submitted as preferred term).
  if (!hasOfferableMarkets && !submissionInProgress && !submissionComplete) {
    return (
      <div className="flex flex-col items-start justify-between gap-6 pb-10">
        <h1 className="m-0 text-4xl">{t('core.earlyPayOffer')}</h1>
        <NoDataState
          className="h-[50vh]"
          icon={<img src={grocerybasket} alt="Grocery Basket" />}
          title={t('builder.noAvailableDivisions')}
          message={t('builder.noAvailableDivisionsDescription')}
          action={
            <div className="flex flex-col items-center gap-2 pt-2">
              <ButtonLink
                className="w-full sm:w-fit"
                variant="secondary"
                to={asEnterpriseLink('find-your-customers/expand-your-network')}
              >
                {t('core.addCustomers')}
              </ButtonLink>
              <ButtonLink className="w-full sm:w-fit" variant="ancillary" to={asEnterpriseLink('customers')}>
                {t('core.returnToHome')}
              </ButtonLink>
            </div>
          }
          dark
        />
      </div>
    );
  }

  return (
    <SetRateModalProvider>
      <InitialStepNavigator />
      <SetRateModalRenderer />
      <div className="flex w-full flex-col items-start justify-between pb-10 md:flex-row md:items-center">
        {isOnConfirmationPage ? (
          <div className="w-full text-center">
            <h1 className="m-0 text-4xl">{t('builder.thankYouForSubmittingOffer')}</h1>
            <div className="mt-4 font-medium">
              {t('offerConfirmation.title', {
                email,
              })}
            </div>
            <div className="mt-1 text-sm text-text-secondary">{t('offerConfirmation.subtitle')}</div>
          </div>
        ) : (
          <div>
            <h1 className="m-0 text-4xl">{t('core.earlyPayOffer')}</h1>
            {!disableNavigation && (
              <div className="mt-2 flex items-center gap-1 text-text-secondary">
                <span className="font-medium">
                  {t('builder.stepNumber', { stepNumber: currentStep.stepNumber, totalSteps })}
                </span>
                <span>{t(currentStep.label)}</span>
              </div>
            )}
          </div>
        )}
        <div ref={portalRef} />
      </div>
      <Outlet />
    </SetRateModalProvider>
  );
};

// creates initial offer builder state from preselected taker markets
const createInitialStateFromPreselection = (
  takerMarkets: TakerMarket[],
  preselectedTakerMarketIds: string[],
  buyers: ReturnType<typeof useGroupByBuyer>,
  options?: OfferBuilderPreselectionOptions
): OfferBuilderState => {
  // get the ids of the preselected taker markets
  const selectedMarkets = takerMarkets.filter((tm) => preselectedTakerMarketIds.includes(tm.id));
  // only prioritize if there are actually selected taker markets
  const shouldPrioritize = (options?.prioritizeSelected ?? false) && selectedMarkets.length > 0;
  const shouldExpand = (options?.expandSelected ?? false) && selectedMarkets.length > 0;
  let initialDisplayOrder;

  if (shouldPrioritize && buyers.length > 0) {
    const selectedIds = new Set(selectedMarkets.map((tm) => tm.id));
    initialDisplayOrder = createPrioritizedDisplayOrder(buyers, selectedIds);
  }

  // use provided working state or create default if there are selected taker markets
  const workingTakerMarkets =
    options?.workingTakerMarkets ?? (selectedMarkets.length > 0 ? ensureWorkingTakerMarkets({}, selectedMarkets) : {});

  return createOfferBuilderInitialState({
    ...options,
    selected: selectedMarkets,
    prioritizeSelected: shouldPrioritize,
    expandSelected: shouldExpand,
    workingTakerMarkets,
    ...(initialDisplayOrder && { initialDisplayOrder }),
  });
};

const OfferBuilderCore = () => {
  const location = useLocation();
  const readOnlyUser = useReadOnlyUser();
  const { data: takerMarkets } = useTakerMarkets((takerMarkets) =>
    takerMarkets.filter(
      (takerMarket) =>
        takerMarket.offerConfig.offerType !== 'PREFERRED_TERM' &&
        takerMarket.marketIsEnabled &&
        !takerMarket.offerConfig.isManuallyAwarded &&
        // An express-accept offer can have all invoices removed from clear, making isManuallyAwarded false even though the offer is still active
        !takerMarket.offerConfig.isActiveExpressAccept
    )
  );
  const buyers = useGroupByBuyer({ takerMarkets: takerMarkets || [], sortByEligible: true });
  const hasOfferableMarkets = !!takerMarkets && takerMarkets.length > 0;

  if (readOnlyUser) {
    return <NavigateWithRedirect to={asEnterpriseLink('customers')} />;
  }

  // check for preselected taker markets from navigation state
  const navigationState = (location.state satisfies OfferBuilderNavigationState | undefined) ?? {};
  const { preselectedTakerMarketIds, ...options } = navigationState;

  // create initial state if taker markets were preselected
  const initialState =
    preselectedTakerMarketIds && preselectedTakerMarketIds.length > 0
      ? createInitialStateFromPreselection(takerMarkets ?? [], preselectedTakerMarketIds, buyers, options)
      : undefined;

  return (
    <OfferBuilderProvider initialState={initialState} takerMarkets={takerMarkets ?? []}>
      <RateTypeSelectPortalProvider>
        <OfferBuilderLayout hasOfferableMarkets={hasOfferableMarkets} />
      </RateTypeSelectPortalProvider>
    </OfferBuilderProvider>
  );
};

const OfferBuilder = () => (
  <QueryBoundaries ErrorComponent={() => <OfferBuilderError />} LoadingComponent={() => <OfferBuilderSkeleton />}>
    <OfferBuilderCore />
  </QueryBoundaries>
);

export default OfferBuilder;
